import * as React from "react";
import { DirectionalHint, ITooltipHostStyles, TooltipHost } from "@fluentui/react";
import authModule from "../../../../../../utilities/AuthModule";
import { fetchWithRetry } from "../../../../../../utilities/FetchRetryUtil";

const defaultTooltipHostStyles: ITooltipHostStyles = {
    root: {
        display: "inline-flex"
    }
}

const altTooltipHostStyles: ITooltipHostStyles = {
    root: {
        display: "inline-flex",
        height: 176,
        width: "100%",
        alignItems: "center"
    }
}

const defaultImageSize: string = "small";

interface IImageRendererProps {
    imageName: string;
    imageItemUrl: string;
    imageSize?: string;
}

export const ImageRenderer: React.FC<IImageRendererProps> = React.memo(({
    imageName,
    imageItemUrl,
    imageSize = defaultImageSize
}) => {
    const [imageUrl, setImageUrl] = React.useState<string>();

    const thumbnailUrl = React.useMemo(() => {
        return `${imageItemUrl}/thumbnails/0/${imageSize}/content?prefer=closestavailablesize`;
    }, [imageItemUrl, imageSize]);

    React.useEffect(() => {
        let isActive = true;
        let objectUrl: string | null = null;

        const fetchImage = async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                const response = await fetchWithRetry(thumbnailUrl, {
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch image: ${response.statusText}`);
                }

                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);

                if (isActive) {
                    setImageUrl(objectUrl);
                }
            } catch (err) {
                if (isActive) {
                    setImageUrl(null);
                }
            }
        };

        fetchImage();

        return () => {
            isActive = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [thumbnailUrl]);

    return !imageUrl
        ? <></>
        : <TooltipHost content={imageName} styles={imageSize === defaultImageSize ? defaultTooltipHostStyles : altTooltipHostStyles} directionalHint={DirectionalHint.topAutoEdge}>
            <img alt={imageName} src={imageUrl} />
        </TooltipHost>
});